import React, { useMemo, useState, FC } from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { AddHoliday } from 'components/dialog/holidays/AddHolidays';
import { EditHoliday } from 'components/dialog/holidays/EditHolidays';
import { InfoHoliday } from 'components/dialog/holidays/InfoHolidays';
import { DuplicateHoliday } from 'components/dialog/holidays/DuplicateHolidays';
import { DeleteHoliday } from 'components/dialog/holidays/DeleteHolidays';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReplayIcon from '@mui/icons-material/Replay';
import { buildSettingsBasePath, buildSettingsCrudPath, parseSettingsCrudRoute } from 'utils/settingsCrudRouting';

interface SubMenuProps {
    selected: any;
    setSelected: () => void;
    rows: any;
    contextMenu: any;
    setContextMenu: (value: any) => void;
    reloading: () => void;
}

const SubMenu: FC<SubMenuProps> = ({ selected, setSelected, rows, contextMenu, setContextMenu, reloading }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    // Dialog states
    const [openDuplicate, setOpenDuplicate] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const basePath = buildSettingsBasePath('holidays');
    const modalRoute = useMemo(() => parseSettingsCrudRoute(location.pathname, 'holidays'), [location.pathname]);
    const openAdd = modalRoute.action === 'add';
    const openEdit = modalRoute.action === 'update' && modalRoute.id !== null;
    const openShow = modalRoute.action === 'detail' && modalRoute.id !== null;

    const selectedRowFromRoute = useMemo(() => {
        if (modalRoute.id === null) return null;
        return rows.find((row: any) => Number(row.id) === modalRoute.id) || null;
    }, [rows, modalRoute.id]);
    const selectedForRoute = selectedRowFromRoute ? [selectedRowFromRoute] : selected;

    const closeRouteModal = () => {
        navigate(basePath);
    };

    // Extract permissions once
    const permissions = React.useMemo(() => JSON.parse(localStorage.getItem('permissions') || '[]') as string[], []);

    // Helpers
    const hasPermission = (perm: string) => permissions.includes(perm);
    const isSingleSelected = selected?.length === 1;
    const hasSelected = selected?.length > 0;

    const options = [
        {
            name: t('settings.holidays.table.context_menu.add'),
            icon: <AddIcon />,
            onClick: () => {
                navigate(buildSettingsCrudPath('holidays', 'add'));
                setContextMenu(null);
            },
            disabled: false,
            hidden: !hasPermission('timekeeper.CreateHoliday'),
            divider: false,
        },
        {
            name: t('settings.holidays.table.context_menu.edit'),
            icon: <EditIcon />,
            onClick: () => {
                const current = selected[0];
                if (!current?.id) return;
                navigate(buildSettingsCrudPath('holidays', 'update', current.id));
                setContextMenu(null);
            },
            disabled: !isSingleSelected,
            hidden: !isSingleSelected || !hasPermission('timekeeper.PatchHolidayById'),
            divider: false,
        },
        {
            name: t('settings.holidays.table.context_menu.info'),
            icon: <VisibilityIcon />,
            onClick: () => {
                const current = selected[0];
                if (!current?.id) return;
                navigate(buildSettingsCrudPath('holidays', 'detail', current.id));
                setContextMenu(null);
            },
            disabled: !hasSelected,
            hidden: !hasSelected || !hasPermission('timekeeper.HolidayById'),
            divider: false,
        },
        {
            name: t('settings.holidays.table.context_menu.duplicate'),
            icon: <ContentCopyIcon />,
            onClick: () => {
                setOpenDuplicate(true);
                setContextMenu(null);
            },
            disabled: !isSingleSelected,
            hidden: !isSingleSelected || !hasPermission('timekeeper.CreateHoliday'),
            divider: false,
        },
        {
            name: t('settings.holidays.table.context_menu.delete'),
            icon: <DeleteIcon color="error" />,
            onClick: () => {
                setOpenDelete(true);
                setContextMenu(null);
            },
            disabled: /* !hasSelected */ true,
            hidden: /* !hasSelected || !hasPermission('timekeeper.DeleteHolidayById') */ false,
            divider: false,
        },
        {
            name: t('settings.holidays.table.context_menu.reload'),
            icon: <ReplayIcon />,
            onClick: () => {
                reloading();
                setContextMenu(null);
            },
            disabled: false,
            hidden: false,
            divider: true,
        },
    ];

    return (
        <>
            <Menu
                open={Boolean(contextMenu)}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                sx={{ boxShadow: 'none' }}
            >
                {options.filter((option) => !option.hidden).map((option) => (
                    <MenuItem key={option.name} onClick={option.onClick} disabled={option.disabled}>
                        <ListItemIcon>{option.icon}</ListItemIcon>
                        <ListItemText primary={option.name} />
                    </MenuItem>
                ))}
            </Menu>

            <AddHoliday open={openAdd} setOpen={(nextOpen: boolean) => { if (!nextOpen) closeRouteModal(); }} rows={rows} reloading={reloading} />
            <EditHoliday open={openEdit} setOpen={(nextOpen: boolean) => { if (!nextOpen) closeRouteModal(); }} rows={rows} reloading={reloading} selected={selectedForRoute} setSelected={setSelected} />
            <InfoHoliday open={openShow} setOpen={(nextOpen: boolean) => { if (!nextOpen) closeRouteModal(); }} selected={selectedForRoute} />
            <DuplicateHoliday open={openDuplicate} setOpen={setOpenDuplicate} rows={rows} reloading={reloading} selected={selected} setSelected={setSelected} />
            <DeleteHoliday open={openDelete} setOpen={setOpenDelete} reloading={reloading} selected={selected} setSelected={setSelected} />
        </>
    );
};

export { SubMenu };
